import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, acceptInvitationSchema } from "@utils/index";
import { AcceptInvitationFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserAcceptInvitation } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormInput,
  Button,
  FormHeaderNotAuth,
  ArrowLeftSvg,
  AuthLinks,
  RegistrationButton,
} from "@components/index";

export const AcceptInvitationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const codeUrlValue = location.pathname.split("/").slice(-1)[0];
  const emailUrlValue = location.search.split("email=")[1];
  const isAuth = useCommonStore(({ isAuth }) => isAuth);
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();

  const { mutate, isPending: isPendingAcceptInvitation } =
    useUserAcceptInvitation(
      () => {
        reset();
        notify("success", "You are invited successfully, please Login.");
        navigate("/login");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "password" | "confirmPassword", {
            message: fields![key],
          }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingAcceptInvitation });
  }, [isPendingAcceptInvitation, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<AcceptInvitationFormInterface>({
    mode: "onBlur",
    disabled: isPendingAcceptInvitation,
    resolver: yupResolver(acceptInvitationSchema),
  });

  useEffect(() => {
    if (codeUrlValue || emailUrlValue) {
      reset({
        code: codeUrlValue,
        email: emailUrlValue,
      });
    }
  }, [reset, emailUrlValue, codeUrlValue]);

  const onSubmit: SubmitHandler<AcceptInvitationFormInterface> = (data) =>
    mutate({ ...data });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="md:mx-auto md:max-w-[440px] md:pt-[218px]"
      >
        <FormHeaderNotAuth
          title="Accept Invitation"
          subTitle="Enter your details below"
        />
        <FormRow top="medium">
          <FormInput
            {...register<"password">("password")}
            name="password"
            type="password"
            label="New password"
            helpText="Your password should include minimum 8 characters, at least 1 uppercase letter, 1 lowercase letter and 1 number"
            error={formState.errors.password?.message}
            onChange={({ currentTarget }) => {
              setValue("password", currentTarget.value.trim());
              clearErrors("password");
            }}
          />
          <FormInput
            {...register<"confirmPassword">("confirmPassword")}
            name="confirmPassword"
            type="password"
            label="Confirm Password"
            error={formState.errors.confirmPassword?.message}
            onChange={({ currentTarget }) => {
              setValue("confirmPassword", currentTarget.value.trim());
              clearErrors("confirmPassword");
            }}
          />
        </FormRow>
        <input type="hidden" {...register("email")} />
        <input type="hidden" {...register("code")} />
        <FormRow top="medium">
          <Button type="submit" title="Submit" />
        </FormRow>
        <FormRow top="small">
          <Link
            to="/login"
            className="mx-auto flex w-fit items-center gap-x-2 text-200 font-bold text-black-54 hover:underline"
          >
            <ArrowLeftSvg />
            Back to sign in
          </Link>
        </FormRow>
        {isMobile && (
          <FormRow top="large">
            <AuthLinks />
          </FormRow>
        )}
      </Form>
      {isMobile && <RegistrationButton />}
      {!isMobile && <AuthLinks />}
    </>
  );
};
